import { createPortal } from 'react-dom';
import { useEffect, useState, type ClipboardEvent, type CSSProperties, type DragEvent, type ReactNode } from 'react';
import { ImagePlus, Sparkles, Plus, X, Check } from 'lucide-react';
import { PageButton, px, INK, INK_SOFT, SEAL } from './SpellBook';
import { TITLE_FONT, PX_FONT, NUM_FONT } from './theme';
import { POINT_VALUES } from '../../lib/pointValues';
import { tradesApi, type ChartDraft, type Trade, type TradeMode, type StatField, type StatFieldDef } from '../../lib/api';

/*
 * The ledger in the classic form's order. Left page: every input — chart and notes, trial, date,
 * session, pair, timeframe, contracts, partials, entry, stop, exit, max adverse, max run, the trader's
 * own stats and rules. Right page: the trade previewed as it's written, Log trade, and the last few entries.
 * History is its own bookmark: the list on the left, the whole trade on the right.
 */

const SESSIONS = ['Asia', 'London', 'NY AM', 'NY PM'];
const today = () => new Date().toISOString().slice(0, 10);
const fmtR = (r: number) => `${r >= 0 ? '+' : ''}${r.toFixed(2)}R`;
const WIN = '#3E6B2E';
const MODE: Record<string, string> = { backtest: 'Replay', paper: 'Paper', live: 'Live' };

const field: CSSProperties = {
  width: '100%', minWidth: 0, boxSizing: 'border-box', background: 'none', border: 'none', borderBottom: `2px dashed ${INK_SOFT}`,
  padding: '1px 5px', fontFamily: NUM_FONT, fontSize: 17, color: INK, outline: 'none',
};
const label: CSSProperties = { fontFamily: PX_FONT, fontSize: 8, letterSpacing: '0.12em', textTransform: 'uppercase', color: INK_SOFT, marginBottom: 1 };
const Labelled = ({ name, children }: { name: string; children: ReactNode }) => (
  <label style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}><span style={label}>{name}</span>{children}</label>
);
const chip = (on: boolean): CSSProperties => ({
  flex: 1, padding: '3px 0', border: 'none', cursor: 'pointer', fontFamily: PX_FONT, fontSize: 9, letterSpacing: '0.04em',
  color: on ? '#FBEBC8' : INK, background: on ? SEAL : 'rgba(255,248,226,0.55)', boxShadow: `0 0 0 2px ${INK}`,
});
const grid = (cols: string): CSSProperties => ({ display: 'grid', gridTemplateColumns: cols, gap: 8, alignSelf: 'stretch', flex: 'none' });
const tick = (on: boolean): CSSProperties => ({ width: 14, height: 14, display: 'grid', placeItems: 'center', boxShadow: `0 0 0 2px ${INK}`, background: on ? SEAL : '#F2E2B6', color: '#FBEBC8', flex: 'none' });
const Toggle = ({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) => (
  <button type="button" onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 6, border: 'none', background: 'none', cursor: 'pointer', fontFamily: PX_FONT, fontSize: 9, color: INK, padding: 0 }}>
    <span style={tick(on)}>{on && <Check size={10} strokeWidth={3} />}</span>{children}
  </button>
);

export interface LedgerState {
  mode: TradeMode; pair: string; date: string; session: string; timeframe: string;
  entry: string; stop: string; run: string; exit: string;
  /** Exit shortcuts: stopped out (exit = stop) or break even (exit = entry) */
  exitAt: 'stop' | 'be' | null;
  qty: string; heat: string; partialOn: boolean; partials: { price: string; qty: string }[];
  followed: boolean; notes: string; context: Record<string, unknown>;
  image: File | null; busy: boolean; note: string | null;
}
export const blankLedger = (mode: TradeMode): LedgerState => ({
  mode, pair: 'MNQ', date: today(), session: 'NY AM', timeframe: '', entry: '', stop: '', run: '', exit: '', exitAt: null,
  qty: '', heat: '', partialOn: false, partials: [], followed: true, notes: '', context: {}, image: null, busy: false, note: null,
});

/** The exit as it stands, honouring the Stop / BE shortcuts */
export const exitOf = (s: LedgerState) => (s.exitAt === 'stop' ? s.stop : s.exitAt === 'be' ? s.entry : s.exit);
/** Partials with both a price and a size (only when the partial box is ticked) */
export const legsOf = (s: LedgerState) => (s.partialOn ? s.partials : []).filter(p => p.price.trim() && p.qty.trim()).map(p => ({ price: Number(p.price), qty: Number(p.qty) }));

/** R for the sheet as it stands (partials blended in by size), or null until all four prices are in */
export function sheetR(s: LedgerState): number | null {
  const ex = exitOf(s);
  const [e, st, x] = [s.entry, s.stop, ex].map(Number);
  if ([s.entry, s.stop, s.run, ex].some(v => v.trim() === '') || ![e, st, x].every(Number.isFinite) || e === st) return null;
  const dir = st < e ? 1 : -1, oneR = Math.abs(e - st), at = (p: number) => (dir * (p - e)) / oneR;
  const legs = legsOf(s), q = Number(s.qty), legQ = legs.reduce((a, l) => a + l.qty, 0);
  return legs.length && q > 0 ? legs.reduce((a, l) => a + (l.qty / q) * at(l.price), 0) + ((q - legQ) / q) * at(x) : at(x);
}

/* ── Left page: the setup ── */

export function LedgerLeft({ s, set, paperOpen, username, fields, catalog, onAddStat }: {
  s: LedgerState; set: (p: Partial<LedgerState>) => void; paperOpen: boolean; username: string; fields: StatField[]; catalog: StatFieldDef[]; onAddStat: (key: string) => void;
}) {
  const [drag, setDrag] = useState(false);
  const have = new Set(fields.map(f => f.key));
  const spare = catalog.filter(c => !have.has(c.key));
  const take = (f: File | undefined | null) => { if (f && f.type.startsWith('image/')) set({ image: f, note: null }); };
  const read = async () => {
    if (!s.image) return;
    set({ busy: true, note: 'The scribe is reading your chart…' });
    try {
      const d: ChartDraft = await tradesApi.readChart(username, s.image);
      const str = (n: number | null | undefined) => (n == null ? '' : String(n));
      set({
        busy: false, note: d.uncertain.length ? `Check: ${d.uncertain.join('; ')}` : 'Drafted from the chart',
        ...(d.pair ? { pair: d.pair.toUpperCase() } : {}), ...(d.traded_on ? { date: d.traded_on } : {}),
        ...(d.session && SESSIONS.includes(d.session) ? { session: d.session } : {}), ...(d.timeframe ? { timeframe: d.timeframe } : {}),
        entry: str(d.entry_price), stop: str(d.stop_price), run: str(d.max_run_price), exit: str(d.exit_price), heat: str(d.max_heat_price),
        ...(d.partials.length ? { partialOn: true, partials: d.partials.map(p => ({ price: String(p.price), qty: p.qty == null ? '' : String(p.qty) })) } : {}),
      });
    } catch (err) { set({ busy: false, note: err instanceof Error ? err.message : 'Could not read that chart' }); }
  };
  const setLeg = (i: number, p: Partial<{ price: string; qty: string }>) => set({ partials: s.partials.map((l, k) => (k === i ? { ...l, ...p } : l)) });
  return (
    <div style={{ alignSelf: 'stretch', display: 'flex', flexDirection: 'column', gap: 5, flex: 1, minHeight: 0 }}>
      {/* Chart and notes, side by side as on the classic form */}
      <div style={grid('1.1fr 1fr')}>
        <div tabIndex={0} onPaste={(e: ClipboardEvent) => take(e.clipboardData.files[0])}
          onDragOver={(e: DragEvent) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
          onDrop={(e: DragEvent) => { e.preventDefault(); setDrag(false); take(e.dataTransfer.files[0]); }}
          style={{
            height: 46, display: 'flex', alignItems: 'center', justifyContent: 'center', outline: 'none', cursor: 'copy',
            border: `2px dashed ${drag ? SEAL : INK_SOFT}`, background: s.image ? `center / cover no-repeat url(${URL.createObjectURL(s.image)})` : 'rgba(255,248,226,0.4)',
          }}>
          {!s.image
            ? <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: PX_FONT, fontSize: 8, color: INK_SOFT, textAlign: 'center' }}><ImagePlus size={13} /> Paste or drop chart</span>
            : <button type="button" onClick={read} disabled={s.busy} style={{ ...chip(false), flex: 'none', padding: '3px 8px', display: 'flex', alignItems: 'center', gap: 5 }}>
                <Sparkles size={10} /> {s.busy ? 'Reading…' : 'Read'}
              </button>}
        </div>
        <textarea placeholder="Notes" value={s.notes} onChange={e => set({ notes: e.target.value })} style={{
          height: 46, boxSizing: 'border-box', resize: 'none', background: 'rgba(255,248,226,0.4)', border: 'none', boxShadow: 'inset 0 0 0 2px rgba(91,58,42,0.35)',
          padding: 5, fontFamily: PX_FONT, fontSize: 9, lineHeight: 1.5, color: INK, outline: 'none',
        }} />
      </div>
      <div style={{ ...grid('1fr 1fr'), padding: '0 2px' }}>
        <button type="button" onClick={() => set({ mode: 'backtest' })} style={chip(s.mode === 'backtest')}>Replay</button>
        <button type="button" disabled={!paperOpen} title={paperOpen ? undefined : 'Opens after the replay trial'} onClick={() => set({ mode: 'paper' })}
          style={{ ...chip(s.mode === 'paper'), opacity: paperOpen ? 1 : 0.45, cursor: paperOpen ? 'pointer' : 'not-allowed' }}>Paper</button>
      </div>
      <div style={grid('1fr')}><Labelled name="Date"><input type="date" style={{ ...field, fontSize: 14 }} value={s.date} onChange={e => set({ date: e.target.value })} /></Labelled></div>
      <div style={{ ...grid('repeat(4, 1fr)'), gap: 5, padding: '0 2px' }}>
        {SESSIONS.map(x => <button key={x} type="button" onClick={() => set({ session: x })} style={chip(s.session === x)}>{x}</button>)}
      </div>
      <div style={grid('1fr 1fr 1fr')}>
        <Labelled name="Pair"><input style={field} value={s.pair} onChange={e => set({ pair: e.target.value.toUpperCase() })} /></Labelled>
        <Labelled name="Timeframe"><input style={field} placeholder="1m" value={s.timeframe} onChange={e => set({ timeframe: e.target.value })} /></Labelled>
        <Labelled name="Contracts"><input inputMode="decimal" style={field} value={s.qty} onChange={e => set({ qty: e.target.value })} /></Labelled>
      </div>
      {/* Partial taken: tick it, then each leg's price and size */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 'none' }}>
        <Toggle on={s.partialOn} onClick={() => set({ partialOn: !s.partialOn, partials: !s.partialOn && !s.partials.length ? [{ price: '', qty: '' }] : s.partials })}>Partial taken</Toggle>
        {s.partialOn && <button type="button" onClick={() => set({ partials: [...s.partials, { price: '', qty: '' }] })} style={{ border: 'none', background: 'none', cursor: 'pointer', color: INK, display: 'flex', alignItems: 'center', gap: 3, fontFamily: PX_FONT, fontSize: 8 }}><Plus size={10} /> another</button>}
      </div>
      {s.partialOn && (
        <div className="sb-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {s.partials.map((l, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 60px 14px', gap: 6, alignItems: 'end', marginBottom: 4 }}>
              <input placeholder="price" inputMode="decimal" style={field} value={l.price} onChange={e => setLeg(i, { price: e.target.value })} />
              <input placeholder="qty" inputMode="decimal" style={field} value={l.qty} onChange={e => setLeg(i, { qty: e.target.value })} />
              <button type="button" aria-label="Remove partial" onClick={() => set({ partials: s.partials.filter((_, k) => k !== i) })} style={{ border: 'none', background: 'none', cursor: 'pointer', color: INK_SOFT, padding: 0 }}><X size={11} /></button>
            </div>
          ))}
          {!s.qty && <span style={{ ...label, color: SEAL }}>Enter contracts so partials can be weighed</span>}
        </div>
      )}
      <div style={grid('1fr 1fr 1fr')}>
        <Labelled name="Entry"><input inputMode="decimal" style={field} value={s.entry} onChange={e => set({ entry: e.target.value })} /></Labelled>
        <Labelled name="Stop"><input inputMode="decimal" style={field} value={s.stop} onChange={e => set({ stop: e.target.value })} /></Labelled>
        <Labelled name="Exit"><input inputMode="decimal" style={field} value={exitOf(s)} disabled={s.exitAt !== null} onChange={e => set({ exit: e.target.value })} /></Labelled>
      </div>
      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', flex: 'none' }}>
        <span style={label}>Exit at</span>
        <Toggle on={s.exitAt === 'stop'} onClick={() => set({ exitAt: s.exitAt === 'stop' ? null : 'stop' })}>Stop</Toggle>
        <Toggle on={s.exitAt === 'be'} onClick={() => set({ exitAt: s.exitAt === 'be' ? null : 'be' })}>BE</Toggle>
      </div>
      <div style={grid('1fr 1fr')}>
        <Labelled name="Max adverse"><input inputMode="decimal" placeholder="optional" style={field} value={s.heat} onChange={e => set({ heat: e.target.value })} /></Labelled>
        <Labelled name="Max run"><input inputMode="decimal" style={field} value={s.run} onChange={e => set({ run: e.target.value })} /></Labelled>
      </div>
      {/* The trader's own stats scroll if there are many */}
      <div className="sb-scroll" style={{ flex: 1, minHeight: 24, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6, paddingRight: 3 }}>
        {fields.map(f => (
          <Labelled key={f.id} name={f.label}>
            <PageStat f={f} value={s.context[f.key]} onChange={v => set({ context: { ...s.context, [f.key]: v } })} />
          </Labelled>
        ))}
        {spare.length > 0 && (
          <select value="" onChange={e => e.target.value && onAddStat(e.target.value)} style={{ ...field, fontFamily: PX_FONT, fontSize: 8, color: INK_SOFT, cursor: 'pointer' }}>
            <option value="">+ Add a stat to track</option>
            {spare.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        )}
      </div>
      <div style={{ flex: 'none' }}><Toggle on={s.followed} onClick={() => set({ followed: !s.followed })}>Followed my rules</Toggle></div>
    </div>
  );
}

/* ── Right page: the outcome, stats, rules, the R, and the last few trades ── */

/** One of the trader's own stat fields, drawn for the page */
function PageStat({ f, value, onChange }: { f: StatFieldDef; value: unknown; onChange: (v: unknown) => void }) {
  if (f.type === 'yesno') return (
    <div style={{ display: 'flex', gap: 10 }}>
      <Toggle on={value === true} onClick={() => onChange(value === true ? undefined : true)}>Yes</Toggle>
      <Toggle on={value === false} onClick={() => onChange(value === false ? undefined : false)}>No</Toggle>
    </div>
  );
  if (f.type === 'choice' || f.type === 'multi') {
    const picks = f.type === 'multi' ? (Array.isArray(value) ? value as string[] : []) : (value ? [String(value)] : []);
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
        {f.options.map(o => {
          const on = picks.includes(o);
          return <button key={o} type="button" onClick={() => onChange(f.type === 'multi' ? (on ? picks.filter(x => x !== o) : [...picks, o]) : (on ? undefined : o))}
            style={{ ...chip(on), flex: 'none', padding: '2px 5px', fontSize: 8 }}>{o}</button>;
        })}
      </div>
    );
  }
  return <input style={field} inputMode={f.type === 'number' || f.type === 'price' ? 'decimal' : undefined} value={String(value ?? '')} onChange={e => onChange(e.target.value)} />;
}

/** Pixel price ladder: stop, entry, exit and max run placed on one scale */
function Ladder({ e, st, x, run }: { e: number; st: number; x: number; run: number }) {
  const lo = Math.min(e, st, x, run), hi = Math.max(e, st, x, run), span = hi - lo || 1;
  const y = (v: number) => 6 + (1 - (v - lo) / span) * 88;
  const mark = (v: number, name: string, color: string) => (
    <g key={name}>
      <line x1={18} x2={70} y1={y(v)} y2={y(v)} stroke={color} strokeWidth={2} strokeDasharray={name === 'run' ? '3 3' : undefined} />
      <text x={74} y={y(v) + 3} fontFamily="Silkscreen, monospace" fontSize={7} fill={color}>{name}</text>
    </g>
  );
  return (
    <svg viewBox="0 0 110 100" style={{ width: '100%', height: 70, flex: 'none' }} shapeRendering="crispEdges">
      <rect x={30} y={Math.min(y(e), y(st))} width={28} height={Math.abs(y(e) - y(st))} fill="rgba(142,47,31,0.18)" />
      <rect x={30} y={Math.min(y(e), y(run))} width={28} height={Math.abs(y(e) - y(run))} fill="rgba(62,107,46,0.16)" />
      {mark(st, 'stop', SEAL)}{mark(run, 'run', '#3E6B2E')}{mark(e, 'entry', INK)}{mark(x, 'exit', '#C98A2E')}
    </svg>
  );
}

/** Right page: the trade as it's being written, the Log trade button, and the last few entries */
export function LedgerPreview({ s, recent, onLog, onSeeHistory }: { s: LedgerState; recent: Trade[]; onLog: () => void; onSeeHistory: () => void }) {
  const r = sheetR(s);
  const [e, st, x, run, heat] = [s.entry, s.stop, exitOf(s), s.run, s.heat].map(Number);
  const ready = r !== null;
  const dir = st < e ? 1 : -1, oneR = Math.abs(e - st);
  const q = Number(s.qty), pv = POINT_VALUES[s.pair] ?? 1, legs = legsOf(s), legQ = legs.reduce((a, l) => a + l.qty, 0);
  const dollars = ready && q > 0 ? (legs.reduce((a, l) => a + dir * (l.price - e) * l.qty, 0) + dir * (x - e) * (q - legQ)) * pv : null;
  const cell = (k: string, v: string, tone?: string) => (
    <div key={k} style={{ textAlign: 'center' }}>
      <div style={{ fontFamily: TITLE_FONT, fontSize: 16, color: tone ?? INK }}>{v}</div>
      <div style={{ ...label, fontSize: 7 }}>{k}</div>
    </div>
  );
  return (
    <div style={{ alignSelf: 'stretch', display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minHeight: 0 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flex: 'none' }}>
        <span style={{ fontFamily: TITLE_FONT, fontSize: 18, color: INK }}>{s.pair || '—'} {ready ? (dir === 1 ? 'long' : 'short') : ''}</span>
        <span style={{ fontFamily: PX_FONT, fontSize: 8, color: INK_SOFT }}>{s.date.slice(5)} · {s.session} · {MODE[s.mode]}</span>
      </div>
      <div style={{ textAlign: 'center', fontFamily: TITLE_FONT, fontSize: 32, lineHeight: 1, color: !ready ? INK_SOFT : r >= 0 ? WIN : SEAL, flex: 'none' }}>{ready ? fmtR(r) : '— R'}</div>
      {ready ? <Ladder e={e} st={st} x={Number(x)} run={run} /> : <div style={{ height: 70, display: 'grid', placeItems: 'center', fontFamily: PX_FONT, fontSize: 9, color: INK_SOFT, flex: 'none' }}>Fill in entry, stop, run and exit</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4, flex: 'none' }}>
        {cell('1R pts', ready ? String(+oneR.toFixed(2)) : '—')}
        {cell('Range', ready ? `${((oneR / e) * 100).toFixed(2)}%` : '—')}
        {cell('Ran', ready ? fmtR((dir * (run - e)) / oneR) : '—', '#3E6B2E')}
        {cell('Heat', ready && s.heat.trim() ? fmtR(-(dir * (e - heat)) / oneR) : '—', SEAL)}
        {cell('P&L', dollars === null ? '—' : `${dollars < 0 ? '-' : ''}$${Math.abs(dollars).toFixed(0)}`, dollars === null ? undefined : dollars >= 0 ? WIN : SEAL)}
        {cell('Partials', String(legs.length))}
      </div>
      {s.note && <div style={{ ...label, textTransform: 'none', letterSpacing: 0, fontSize: 9, flex: 'none' }}>{s.note}</div>}
      <div style={{ display: 'flex', justifyContent: 'center', flex: 'none', marginTop: 'auto' }}>
        <PageButton onClick={() => !s.busy && ready && onLog()}>
          {s.busy ? 'Saving…' : ready ? <span style={{ color: r >= 0 ? WIN : SEAL }}>Log trade · {fmtR(r)}</span> : 'Log trade'}
        </PageButton>
      </div>
      <div style={{ flex: 'none', borderTop: '2px dotted rgba(91,58,42,0.4)', paddingTop: 3 }}>
        {recent.slice(0, 3).map(t => <HistoryRow key={t.id} t={t} compact />)}
        <button type="button" onClick={onSeeHistory} style={{ width: '100%', marginTop: 2, border: 'none', background: 'none', cursor: 'pointer', fontFamily: PX_FONT, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: SEAL }}>See history →</button>
      </div>
    </div>
  );
}

/* ── History ── */

function HistoryRow({ t, on, onClick, compact }: { t: Trade; on?: boolean; onClick?: () => void; compact?: boolean }) {
  const r = Number(t.r_multiple);
  return (
    <button type="button" onClick={onClick} disabled={!onClick} style={{
      width: '100%', display: 'grid', gridTemplateColumns: '44px 1fr auto', alignItems: 'center', gap: 6, padding: compact ? '1px 2px' : '5px 6px',
      background: on ? 'rgba(142,47,31,0.08)' : 'none', border: 'none', borderBottom: compact ? 'none' : '2px dotted rgba(91,58,42,0.35)',
      cursor: onClick ? 'pointer' : 'default', textAlign: 'left', fontFamily: PX_FONT, fontSize: compact ? 8 : 10, color: INK, position: 'relative',
    }}>
      {on && <span style={{ position: 'absolute', left: -10, top: '50%', marginTop: -5, borderLeft: `7px solid ${SEAL}`, borderTop: '5px solid transparent', borderBottom: '5px solid transparent' }} />}
      <span style={{ color: INK_SOFT }}>{t.traded_on.slice(5)}</span>
      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.pair} · {MODE[t.mode] ?? t.mode}</span>
      <span style={{ fontFamily: TITLE_FONT, fontSize: compact ? 12 : 15, color: r >= 0 ? WIN : SEAL }}>{fmtR(r)}</span>
    </button>
  );
}

export function HistoryList({ trades, pick, onPick }: { trades: Trade[]; pick: number | null; onPick: (id: number) => void }) {
  return (
    <div className="sb-scroll" style={{ alignSelf: 'stretch', flex: 1, minHeight: 0, overflowY: 'auto', paddingLeft: 10, paddingRight: 4, marginTop: 6 }}>
      {trades.length === 0 && <p style={{ fontFamily: PX_FONT, fontSize: 11, color: INK_SOFT, textAlign: 'center', marginTop: px(10) }}>No trades inscribed yet.</p>}
      {trades.map(t => <HistoryRow key={t.id} t={t} on={t.id === pick} onClick={() => onPick(t.id)} />)}
    </div>
  );
}

/** The whole of one trade, as its own page */
export function TradeDetail({ t, username, onRemoved }: { t: Trade | undefined; username: string; onRemoved: () => void }) {
  const [img, setImg] = useState<string | null>(null);
  const [zoom, setZoom] = useState(false);
  const [striking, setStriking] = useState(false); // asking to confirm the erase
  useEffect(() => {
    setImg(null);
    setStriking(false);
    if (!t) return;
    let url: string | null = null;
    tradesApi.imageUrl(username, t.id).then(u => { url = u; setImg(u); }).catch(() => undefined);
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [t?.id, username]);
  if (!t) return <p style={{ fontFamily: PX_FONT, fontSize: 11, color: INK_SOFT, marginTop: px(20) }}>Pick a trade from the list.</p>;
  const r = Number(t.r_multiple);
  const n = (v: string | null) => (v == null ? '—' : String(Number(v))); // prices arrive with trailing zeros
  const row = (k: string, v: ReactNode) => (
    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontFamily: PX_FONT, fontSize: 9, color: INK, padding: '2px 0', borderBottom: '1px dotted rgba(91,58,42,0.3)' }}>
      <span style={{ color: INK_SOFT, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{k}</span><span>{v}</span>
    </div>
  );
  const ctx = Object.entries(t.context ?? {}).filter(([, v]) => v !== null && v !== undefined && v !== '');
  return (
    <div className="sb-scroll" style={{ alignSelf: 'stretch', flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: 4 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <span style={{ fontFamily: TITLE_FONT, fontSize: 20, color: INK }}>{t.pair} {t.direction}</span>
        <span style={{ fontFamily: TITLE_FONT, fontSize: 20, color: r >= 0 ? WIN : SEAL }}>{fmtR(r)}</span>
      </div>
      {row('Date', `${t.traded_on} · ${t.session ?? '—'}`)}
      {row('Trial', `${MODE[t.mode] ?? t.mode} · ${t.strategy}${t.timeframe ? ` · ${t.timeframe}` : ''}`)}
      {row('Entry / stop', `${n(t.entry_price)} / ${n(t.stop_price)}`)}
      {row('Exit', n(t.exit_price))}
      {row('Max run', t.max_run_r ? fmtR(Number(t.max_run_r)) : '—')}
      {row('Max adverse', t.max_heat_r ? fmtR(-Math.abs(Number(t.max_heat_r))) : '—')}
      {t.quantity && row('Contracts', n(t.quantity))}
      {(t.partials ?? []).map((p, i) => row(`Partial ${i + 1}`, `${p.qty} @ ${p.price}`))}
      {t.pnl_dollars !== null && row('P&L', `$${t.pnl_dollars.toFixed(2)}`)}
      {row('Rules', t.followed_rules ? 'kept' : 'broken')}
      {ctx.map(([k, v]) => row(k.replace(/_/g, ' '), Array.isArray(v) ? v.join(', ') : typeof v === 'boolean' ? (v ? 'yes' : 'no') : String(v)))}
      {t.notes && <p style={{ fontFamily: PX_FONT, fontSize: 9, lineHeight: 1.6, color: INK, margin: '6px 0 0', whiteSpace: 'pre-wrap' }}>{t.notes}</p>}
      {/* The whole chart as pasted, at the foot of the page; click for full size */}
      {img && (
        <button type="button" onClick={() => setZoom(true)} title="View full size" style={{ display: 'block', width: '100%', marginTop: 8, padding: 0, border: 'none', background: 'none', cursor: 'zoom-in' }}>
          <img src={img} alt="Trade chart" style={{ display: 'block', width: '100%', height: 'auto', boxShadow: `0 0 0 2px ${INK}` }} />
        </button>
      )}
      {/* Erase, with a confirm step so a stray click can't lose a trade */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8, fontFamily: PX_FONT, fontSize: 9 }}>
        {!striking
          ? <button type="button" onClick={() => setStriking(true)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: INK_SOFT, textDecoration: 'line-through', fontFamily: PX_FONT, fontSize: 9 }}>Strike from ledger</button>
          : <>
              <span style={{ color: SEAL }}>Erase this trade for good?</span>
              <button type="button" onClick={() => tradesApi.remove(username, t.id).then(onRemoved).catch(() => setStriking(false))} style={{ border: 'none', background: SEAL, color: '#FBEBC8', cursor: 'pointer', fontFamily: PX_FONT, fontSize: 9, padding: '2px 8px' }}>Erase</button>
              <button type="button" onClick={() => setStriking(false)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: INK, fontFamily: PX_FONT, fontSize: 9 }}>Keep</button>
            </>}
      </div>
      {/* Portalled out: the book's transform and page clipping would otherwise trap the overlay */}
      {zoom && img && createPortal(
        <div role="dialog" aria-label="Trade chart" onClick={() => setZoom(false)} style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(3,10,15,0.85)', display: 'grid', placeItems: 'center', cursor: 'zoom-out', padding: 24 }}>
          <img src={img} alt="Trade chart" style={{ maxWidth: '100%', maxHeight: '100%', boxShadow: `0 0 0 3px ${INK}, 0 0 0 6px #EEDDB2` }} />
        </div>,
        document.body,
      )}
    </div>
  );
}
